import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Image } from "react-bootstrap";
import { Button, Rate, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Common/Loader";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ProgressPicturesModal from "./ProgressPicturesModal";
import { fetchClientCheckInDetails } from "../../redux/Slices/clientCheckInSlice";
import placeholderFrontImage from "../../styles/Assets/placeholderFrontImage.png";
import placeholderBackImage from "../../styles/Assets/placeholderBackImage.png";
import placeholderSideImage from "../../styles/Assets/placeholderSideImage.png";
import { UnitContext } from "../../hooks/UnitProvider";
import { formatMeasurement, formatWeight } from "../../utils/unitConversion";
import { formatDate, isoDateFormat } from "../../utils/Utils";
import ToastNotification from "../../components/Common/ToastNotification";
import { CONSTANTS } from "../../utils/Constant/Constant";
import { updateCheckInReadStatusAPI } from "../../api/clientCheckInAPI";
import FormLabel from "../../components/Common/FormLabel";

const ClientCheckInsDetails = ({ selectedClientId, status }) => {
  const dispatch = useDispatch();
  const { unit } = useContext(UnitContext);

  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;

  const { clientCheckInData, loading, error } = useSelector(
    (state) => state.clientCheckIn
  );

  const { success: updateSuccess } = useSelector((state) => state.weightGoal);

  const { trans } = useCustomTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("front");
  const [toastConfig, setToastConfig] = useState(null);
  const [showConfirmationButton, setShowConfirmationButton] = useState(false);
  const moodEmojis = {
    1: "☹️", // face-frown
    2: "😐", // face-meh
    3: "😊", // face-smile
    4: "😁", // face-grin
    5: "😍", // face-grin-hearts
  };

  useEffect(() => {
    if (coachId && selectedClientId) {
      const clientId = selectedClientId;
      const payload = { coachId, clientId };

      dispatch(fetchClientCheckInDetails(payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedClientId, coachId, updateSuccess]);

  const handlePreviousDate = () => {
    const selectedDateOnly =
      clientCheckInData?.previousDateCheckInEntry &&
      isoDateFormat(clientCheckInData?.previousDateCheckInEntry);

    const checkInDetailsId = clientCheckInData?.previousDateCheckInDetailsId;

    if (selectedDateOnly !== "Invalid date" && coachId && selectedClientId) {
      const clientId = selectedClientId;
      const payload = { coachId, clientId, selectedDateOnly, checkInDetailsId };
      dispatch(fetchClientCheckInDetails(payload));
      setShowConfirmationButton(false);
    } else {
      setToastConfig({
        type: CONSTANTS.TOAST_WARNING,
        message: trans("ClientOverviewTab.noPreviousCheckInDetailsAvailable"),
        heading: "",
      });
      setTimeout(() => {
        setToastConfig(null);
      }, 3000);
    }
  };

  const handleNextDate = () => {
    const selectedDateOnly =
      clientCheckInData?.nextDateCheckInEntry &&
      isoDateFormat(clientCheckInData?.nextDateCheckInEntry);

    const checkInDetailsId =
      clientCheckInData?.nextDateCheckInDetailsId || null;

    if (selectedDateOnly !== "Invalid date" && coachId && selectedClientId) {
      const clientId = selectedClientId;
      const payload = { coachId, clientId, selectedDateOnly, checkInDetailsId };
      dispatch(fetchClientCheckInDetails(payload));
      setShowConfirmationButton(false);
    } else {
      setToastConfig({
        type: "warning",
        message: trans("ClientOverviewTab.noNextCheckInDetailsAvailable"),
        heading: "",
      });
      setTimeout(() => {
        setToastConfig(null);
      }, 3000);
    }
  };
  const openModal = (active) => {
    setIsModalVisible(true);

    setActiveTab(active);
  };
  const closeModal = () => setIsModalVisible(false);

  const handleCheckInClick = () => {
    const payload = {
      coachId: coachId,
      CheckInDetailsId: clientCheckInData.checkInDetailsId,
    };
    updateCheckInReadStatusAPI(payload).then((response) => {
      response?.status === "Success" && setShowConfirmationButton(true);
    });
  };
  return (
    <div>
      {loading ? (
        !error ? (
          <Loader />
        ) : (
          <div className="my-5 d-flex flex-column justify-content-center align-items-center fw-normal">
            <p className="mt-3 fs-5">No check-in details available</p>
          </div>
        )
      ) : (
        <Row>
          <Row className="align-items-center">
            <Col>
              <h6>{trans("ClientOverviewTab.CheckIn")}</h6>
            </Col>
            <Col className="text-end p-0">
              {/* <i className="bi bi-pencil"></i> */}
              {clientCheckInData.checkInDetailsId ===
                "00000000-0000-0000-0000-000000000000" && (
                <FormLabel label="Not yet Check-In" className="fs-6 " />
              )}
              {!clientCheckInData?.isread &&
              !showConfirmationButton &&
              clientCheckInData.checkInDetailsId !==
                "00000000-0000-0000-0000-000000000000" ? (
                <div>
                  <Button
                    className="saveButton color-white m-1"
                    onClick={() => handleCheckInClick()}
                    disabled={status}
                  >
                    {"Mark Check-In as Seen"}
                  </Button>
                </div>
              ) : (
                clientCheckInData.checkInDetailsId !==
                  "00000000-0000-0000-0000-000000000000" && (
                  <FormLabel
                    label="Check-In Reviewed"
                    className="fs-6 text-success"
                  />
                )
              )}
            </Col>
          </Row>
          <Col xs={6} sm={6} md={6} lg={6}>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ">
              <Col xs={8}>
                <p className="ms-1 fs-6 font-bold mb-1">
                  {clientCheckInData?.updatedDate &&
                    formatDate(clientCheckInData?.updatedDate)}
                </p>
                <p className="ms-1 fs-6 font-normal">
                  {`Week ${clientCheckInData?.currentSubscriptionWeeks} of ${clientCheckInData?.totalSubscriptionWeeks}`}
                </p>
              </Col>
              <Col
                xs={4}
                className="d-flex align-items-center justify-content-space-between"
              >
                {/* Back and Forward arrows */}
                <span
                  className="cursor-pointer me-2"
                  onClick={() => handlePreviousDate()}
                >
                  <i className="bi bi-caret-left-fill bg-white p-1"></i>
                </span>
                <span
                  className="cursor-pointer"
                  onClick={() => handleNextDate()}
                >
                  <i className="bi bi-caret-right-fill bg-white p-1"></i>
                </span>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2">
              <Col>
                <p className="ms-1 fs-6 font-normal  text-color-gray-40 m-0">
                  {trans("Common.weight")}
                </p>
                <p className="ms-1 fs-6 font-bold">
                  {`${
                    clientCheckInData.weight
                      ? formatWeight(unit, clientCheckInData.weight)
                      : "-"
                  }`}
                  {/* <span style={{ color: "#1890ff" }}>-0.3</span> */}
                </p>
              </Col>
              <Col>
                <p className="ms-1 fs-6 font-normal  text-color-gray-40 m-0">
                  {trans("Common.goal") + " "}
                  <i className="bi bi-flag-fill"></i>
                </p>
                <p className="ms-1 fs-6 font-bold">{`${
                  clientCheckInData?.targetWeight
                    ? formatWeight(unit, clientCheckInData?.targetWeight)
                    : "-"
                }`}</p>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 p-3 mb-1">
              <Col xs={12} className="p-0">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="ms-1 fs-6 font-bold m-0">
                    {trans("Common.progress") + " " + trans("Common.pictures")}
                  </p>
                </div>
                <div className="d-flex gap-1 ms-0">
                  <Col xs={4} sm={4} md={4} lg={4}>
                    <div className="position-relative">
                      <Tooltip
                        title={trans("ClientOverviewTab.clickToCompare")}
                      >
                        <Image
                          src={`${clientCheckInData?.progressPictures?.latestCheckInFrontImage?.url}`}
                          alt={
                            trans("Common.profile") +
                            " " +
                            trans("Common.pictures")
                          }
                          className="client-progress-pictures-images"
                          onClick={() => openModal("front")}
                          onError={(e) =>
                            (e.target.src = placeholderFrontImage)
                          }
                        />
                      </Tooltip>
                      <p className="fs-6 font-bold d-flex mt-1 justify-content-center">
                        Front 
                      </p>
                    </div>
                  </Col>
                  <Col xs={4} sm={4} md={4} lg={4}>
                    <div className="position-relative">
                      <Tooltip
                        title={trans("ClientOverviewTab.clickToCompare")}
                      >
                        <Image
                          src={`${clientCheckInData?.progressPictures?.latestCheckInSideImage?.url}`}
                          alt={
                            trans("Common.profile") +
                            " " +
                            trans("Common.pictures")
                          }
                          className="client-progress-pictures-images"
                          onClick={() => openModal("side")}
                          onError={(e) => (e.target.src = placeholderSideImage)}
                        />
                      </Tooltip>
                      <p className="fs-6 font-bold d-flex mt-1 justify-content-center">
                        Side
                      </p>
                    </div>
                  </Col>
                  <Col xs={4} sm={4} md={4} lg={4}>
                    <div className="position-relative">
                      <Tooltip
                        title={trans("ClientOverviewTab.clickToCompare")}
                      >
                        <Image
                          src={`${clientCheckInData?.progressPictures?.latestCheckInBackImage?.url}`}
                          alt={
                            trans("Common.profile") +
                            " " +
                            trans("Common.pictures")
                          }
                          className="client-progress-pictures-images"
                          onClick={() => openModal("back")}
                          onError={(e) => (e.target.src = placeholderBackImage)}
                        />
                      </Tooltip>
                      <p className="fs-6 font-bold d-flex mt-1 justify-content-center">
                        Back
                      </p>
                    </div>
                  </Col>
                </div>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 p-1 mb-1">
              <Col xs={6} sm={6} md={6} lg={6}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1">
                  {trans("ClientOverviewTab.energyLevel")}
                </p>
                <Rate
                  value={clientCheckInData?.energyLevel}
                  disabled
                  className="color-yellow fs-6"
                />
              </Col>
              <Col xs={6} sm={6} md={6} lg={6}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1 text-color-gray-40">
                  {trans("ClientOverviewTab.previously")}
                </p>
                <Rate
                  value={clientCheckInData?.previousEnergyLevel}
                  disabled
                  className="color-yellow fs-6 opacity-50 p-0 m-0"
                />
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 p-1">
              <Col xs={6} sm={6} md={6} lg={6}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1">
                  {trans("ClientOverviewTab.mood")}
                </p>
                <p className="ms-1 fs-6 font-bold m-0 mt-1 d-flex">
                  {Object.entries(moodEmojis).map(([mood, emoji]) => (
                    <Tooltip key={mood} title={mood}>
                      <span
                        className={`fs-4 ${
                          clientCheckInData?.mood === mood
                            ? "color-primary opacity-100"
                            : "color-gray-40 opacity-50"
                        } pointer-events-none`}
                      >
                        {emoji}
                      </span>
                    </Tooltip>
                  ))}
                </p>
              </Col>
              <Col xs={4} sm={4} md={4} lg={4}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1 text-color-gray-40">
                  {trans("ClientOverviewTab.previously")}
                </p>
                <Tooltip title="Current Mood">
                  <span className="text-color-gray-40 opacity-50 fs-4">
                    {moodEmojis[clientCheckInData?.previousMood]}
                  </span>
                </Tooltip>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 p-1 mt-1">
              <Col xs={6} sm={6} md={6} lg={6}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1">
                  {trans("ClientOverviewTab.mealPlanUse")}
                </p>
                <Rate
                  value={clientCheckInData?.mealPlanUse}
                  disabled
                  className="color-yellow fs-6"
                />
              </Col>
              <Col xs={6} sm={6} md={6} lg={6}>
                <p className="ms-1 fs-6 font-bold m-0 mt-1">
                  {trans("ClientOverviewTab.workoutPlanUse")}
                </p>
                <Rate
                  value={clientCheckInData?.workoutPlanUse}
                  disabled
                  className="color-yellow fs-6"
                />
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 mt-1">
              <Col>
                <p className="ms-1 fs-6 font-bold mb-1">
                  {trans("ClientOverviewTab.digestion")}
                </p>
                <p className="ms-1 fs-6 font-normal">
                  {clientCheckInData?.digestion || "-"}
                </p>
              </Col>
            </Row>
          </Col>
          <Col xs={6} sm={6} md={6} lg={6}>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <Col>
                <p className="ms-1 fs-6 font-bold mb-1">
                  {trans("Common.status")}
                </p>
                <p className="ms-1 fs-6 font-normal">
                  {clientCheckInData?.status || "-"}
                </p>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <Col>
                <p className="ms-1 fs-6 font-bold mb-1">
                  {trans("ClientOverviewTab.bodyMeasurement")}
                </p>
                <p className="ms-1 fs-6 font-normal">{`${
                  clientCheckInData?.weight
                    ? formatWeight(unit, clientCheckInData?.weight)
                    : "-"
                }`}</p>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <Col>
                <p className="ms-1 fs-6 font-bold mb-1">
                  {trans("ClientOverviewTab.circumferences")}
                </p>
                <Row>
                  <Col>
                    <p className="ms-1 fs-6 font-normal mb-1">
                      {trans("ClientOverviewTab.arm")}
                    </p>
                    <p className="ms-1 fs-6 font-normal">{`${
                      clientCheckInData?.circumferences?.arm
                        ? formatMeasurement(
                            unit,
                            clientCheckInData?.circumferences?.arm
                          )
                        : "-"
                    }`}</p>
                  </Col>
                  <Col>
                    <p className="ms-1 fs-6 font-normal mb-1">
                      {trans("ClientOverviewTab.chest")}
                    </p>
                    <p className="ms-1 fs-6 font-normal">{`${
                      clientCheckInData?.circumferences?.chest
                        ? formatMeasurement(
                            unit,
                            clientCheckInData?.circumferences?.chest
                          )
                        : "-"
                    }`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="ms-1 fs-6 font-normal mb-1">
                      {trans("ClientOverviewTab.shoulder")}
                    </p>
                    <p className="ms-1 fs-6 font-normal">{`${
                      clientCheckInData?.circumferences?.shoulder
                        ? formatMeasurement(
                            unit,
                            clientCheckInData?.circumferences?.shoulder
                          )
                        : "-"
                    }`}</p>
                  </Col>
                  <Col>
                    <p className="ms-1 fs-6 font-normal mb-1">
                      {trans("ClientOverviewTab.waist")}
                    </p>
                    <p className="ms-1 fs-6 font-normal">{`${
                      clientCheckInData?.circumferences?.waist
                        ? formatMeasurement(
                            unit,
                            clientCheckInData?.circumferences?.waist
                          )
                        : "-"
                    }`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="ms-1 fs-6 font-normal mb-1">
                      {trans("ClientOverviewTab.thigh")}
                    </p>
                    <p className="ms-1 fs-6 font-normal">{`${
                      clientCheckInData?.circumferences?.thigh
                        ? formatMeasurement(
                            unit,
                            clientCheckInData?.circumferences?.thigh
                          )
                        : "-"
                    }`}</p>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <p className="ms-1 fs-6 font-bold mb-1">
                {trans("ClientOverviewTab.sleep")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {clientCheckInData?.sleep || "-"}
              </p>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <p className="ms-1 fs-6 font-bold mb-1">
                {trans("ClientOverviewTab.waterIntake")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {clientCheckInData?.waterIntake || "-"}
              </p>
            </Row>
            <Row className="bg-gray-100 rounded-3 mb-1 pt-2 ms-1">
              <p className="ms-1 fs-6 font-bold mb-1">
                {trans("ClientOverviewTab.steps")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {clientCheckInData?.steps || "-"}
              </p>
            </Row>
          </Col>
        </Row>
      )}

      <ProgressPicturesModal
        visible={isModalVisible}
        onClose={closeModal}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        clientCheckInData={clientCheckInData}
      />
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientCheckInsDetails;
